import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  LockReset as LockResetIcon,
  ContentCopy as CopyIcon,
  Security as SecurityIcon,
  HelpOutline as HelpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  getAllTenants,
  createTenant,
  getTenantProfiles,
  updateTenant,
  deleteTenant,
  getTenantUsers,
  getTenantEmailSettings,
  updateTenantEmailSettings,
  resetTenantUserPassword,
  getTenantAuthSettings,
  updateTenantAuthSettings,
} from '../api';
import { useAuth } from '../context/AuthContext';

// Tooltip body for the Starting profile info icon.
function ProfileSummary({ profile }) {
  if (!profile) return '';
  return (
    <Box>
      <Typography variant="body2">
        Sets once, at creation — every item can be changed later in the tenant's Company Setup:
      </Typography>
      <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
        {profile.summary.map((line) => <li key={line}>{line}</li>)}
      </Box>
    </Box>
  );
}

function TenantManagement() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [emailSettings, setEmailSettings] = useState(null);
  const [authSettings, setAuthSettings] = useState(null);
  const [authSaving, setAuthSaving] = useState(false);
  
  // Form state
  const [resetResult, setResetResult] = useState(null); // { username, tempPassword, emailSent }
  const [resettingUserId, setResettingUserId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    is_active: true,
    admin_username: '',
    admin_password: '',
    admin_email: '',
    profile: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [profilesFailed, setProfilesFailed] = useState(false);

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllTenants();
      setTenants(response.data);
    } catch (err) {
      setError('Failed to load tenants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Clear messages after a delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Only program_admin can access this
  if (user?.role !== 'program_admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Only program administrators can access this page.
        </Alert>
      </Box>
    );
  }

  const handleOpenEditDialog = (tenant = null) => {
    if (tenant) {
      setFormData({
        name: tenant.slug,
        display_name: tenant.display_name,
        is_active: tenant.is_active,
        admin_username: '',
        admin_password: '',
        admin_email: '',
        profile: '',
      });
      setSelectedTenant(tenant);
    } else {
      setFormData({
        name: '',
        display_name: '',
        is_active: true,
        admin_username: '',
        admin_password: '',
        admin_email: '',
        profile: '',
      });
      setSelectedTenant(null);
      setProfiles([]);
      setProfilesFailed(false);
      getTenantProfiles()
        .then((res) => {
          const list = res.data.profiles || [];
          const def = list.find((p) => p.default);
          setProfiles(list);
          setProfilesFailed(false);
          if (def) setFormData((f) => (f.profile ? f : { ...f, profile: def.key }));
        })
        .catch(() => {
          setProfiles([]);
          setProfilesFailed(true);
        });
    }
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedTenant(null);
    setFormData({
      name: '',
      display_name: '',
      is_active: true,
      admin_username: '',
      admin_password: '',
      admin_email: '',
      profile: '',
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name/slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.name)) {
      errors.name = 'Name can only contain lowercase letters, numbers, and hyphens';
    }
    if (!formData.display_name.trim()) {
      errors.display_name = 'Display name is required';
    }
    // For new tenant, admin credentials are optional but if username is provided, password is required
    if (!selectedTenant && formData.admin_username && !formData.admin_password) {
      errors.admin_password = 'Password is required when creating admin user';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTenant = async () => {
    if (!validateForm()) return;

    try {
      if (selectedTenant) {
        // Update existing tenant
        await updateTenant(selectedTenant.id, {
          display_name: formData.display_name,
          is_active: formData.is_active,
        });
        setSuccess('Tenant updated successfully');
      } else {
        // Create new tenant
        const createData = {
          name: formData.display_name, // Use display name as the name
          slug: formData.name, // Use the "name" field as slug (it's validated as slug format)
          display_name: formData.display_name,
          is_active: formData.is_active,
        };
        if (formData.profile) createData.profile = formData.profile;
        if (formData.admin_username) {
          createData.admin_username = formData.admin_username;
          createData.admin_password = formData.admin_password;
          createData.admin_email = formData.admin_email;
        }
        await createTenant(createData);
        setSuccess('Tenant created successfully');
      }
      handleCloseEditDialog();
      loadTenants();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save tenant');
    }
  };

  const handleOpenUsersDialog = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const response = await getTenantUsers(tenant.id);
      setTenantUsers(response.data);
      setUsersDialogOpen(true);
    } catch (err) {
      setError('Failed to load tenant users');
    }
  };

  const handleOpenEmailDialog = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const response = await getTenantEmailSettings(tenant.id);
      const data = response.data || {};
      setEmailSettings({
        email_provider: data.email_provider || 'smtp',
        enabled: data.enabled || false,
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        smtp_secure: data.smtp_secure || false,
        smtp_user: data.smtp_user || '',
        smtp_pass: '',
        from_email: data.from_email || '',
        from_name: data.from_name || '',
        resend_api_key: '',
        resend_from_domain: data.resend_from_domain || '',
        graph_tenant_id: data.graph_tenant_id || '',
        graph_client_id: data.graph_client_id || '',
        graph_client_secret: '',
        graph_sender: data.graph_sender || '',
      });
      setEmailDialogOpen(true);
    } catch (err) {
      setError('Failed to load email settings');
    }
  };

  const handleSaveEmailSettings = async () => {
    try {
      await updateTenantEmailSettings(selectedTenant.id, emailSettings);
      setSuccess('Email settings updated successfully');
      setEmailDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save email settings');
    }
  };

  const handleOpenAuthDialog = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const response = await getTenantAuthSettings(tenant.id);
      setAuthSettings(response.data || {
        auth_method: 'local',
        mfa_required: false,
        mfa_admin_required: false,
        oidc_provider: 'entra',
        oidc_client_id: '',
        oidc_client_secret: '',
        oidc_tenant_id: '',
        oidc_auto_provision: true,
      });
      setAuthDialogOpen(true);
    } catch (err) {
      setError('Failed to load auth settings');
    }
  };

  const handleSaveAuthSettings = async () => {
    setAuthSaving(true);
    try {
      await updateTenantAuthSettings(selectedTenant.id, authSettings);
      setSuccess('Authentication settings updated successfully');
      setAuthDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save auth settings');
    } finally {
      setAuthSaving(false);
    }
  };

  const handleDeleteTenant = async () => {
    try {
      await deleteTenant(selectedTenant.id);
      setSuccess('Tenant deactivated successfully');
      setDeleteDialogOpen(false);
      loadTenants();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete tenant');
    }
  };

  const handleResetUserPassword = async (tenantId, userId) => {
    setResettingUserId(userId);
    try {
      const response = await resetTenantUserPassword(tenantId, userId);
      setResetResult({
        username: response.data.username,
        tempPassword: response.data.tempPassword,
        emailSent: response.data.emailSent,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResettingUserId(null);
    }
  };

  // Mobile card view for tenants
  const renderMobileView = () => (
    <Stack spacing={1.5}>
      {tenants.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No tenants found. Create your first tenant to get started.
          </Typography>
        </Paper>
      ) : (
        tenants.map((tenant) => (
          <Card key={tenant.id} variant="outlined">
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              {/* Header with name and status */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                  <Typography fontWeight="medium">
                    {tenant.display_name}
                  </Typography>
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                    {tenant.slug}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {tenant.subscription_tier === 'trial' && (
                    <Chip
                      label={tenant.trial_ends_at && new Date(tenant.trial_ends_at) < new Date() ? 'Trial Expired' : `Trial${tenant.trial_ends_at ? ` (ends ${new Date(tenant.trial_ends_at).toLocaleDateString()})` : ''}`}
                      color={tenant.trial_ends_at && new Date(tenant.trial_ends_at) < new Date() ? 'error' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {tenant.subscription_tier && tenant.subscription_tier !== 'trial' && (
                    <Chip
                      label={tenant.subscription_tier.charAt(0).toUpperCase() + tenant.subscription_tier.slice(1)}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={tenant.is_active ? 'Active' : 'Inactive'}
                    color={tenant.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Stats */}
              <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Users:</strong> {tenant.user_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Employees:</strong> {tenant.employee_count || 0}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" mb={1.5}>
                <strong>Created:</strong> {new Date(tenant.created_at).toLocaleDateString()}
              </Typography>
              
              {/* Action buttons */}
              <Box display="flex" justifyContent="flex-end" gap={0.5} flexWrap="wrap">
                {tenant.subscription_tier === 'trial' && (
                  <Tooltip title="Remove trial restrictions — grants Business tier with no expiration or employee limits">
                    <Button
                      size="small"
                      color="success"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          await updateTenant(tenant.id, { grant_full_access: true });
                          setSuccess(`Full access granted to ${tenant.display_name}`);
                          loadTenants();
                        } catch (err) {
                          setError(err.response?.data?.error || 'Failed to grant access');
                        }
                      }}
                    >
                      Grant Full Access
                    </Button>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  startIcon={<PeopleIcon />}
                  onClick={() => handleOpenUsersDialog(tenant)}
                >
                  Users
                </Button>
                <Button
                  size="small"
                  startIcon={<EmailIcon />}
                  onClick={() => handleOpenEmailDialog(tenant)}
                >
                  Email
                </Button>
                <Button
                  size="small"
                  startIcon={<SecurityIcon />}
                  onClick={() => handleOpenAuthDialog(tenant)}
                >
                  Auth
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenEditDialog(tenant)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setSelectedTenant(tenant);
                    setDeleteDialogOpen(true);
                  }}
                  disabled={!tenant.is_active}
                >
                  Deactivate
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  );

  // Desktop table view for tenants
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Name/Slug</strong></TableCell>
            <TableCell><strong>Display Name</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Users</strong></TableCell>
            <TableCell><strong>Employees</strong></TableCell>
            <TableCell><strong>Created</strong></TableCell>
            <TableCell align="right"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tenants.map((tenant) => (
            <TableRow key={tenant.id} hover>
              <TableCell>
                <Typography variant="body2" fontFamily="monospace">
                  {tenant.slug}
                </Typography>
              </TableCell>
              <TableCell>{tenant.display_name}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={tenant.is_active ? 'Active' : 'Inactive'}
                    color={tenant.is_active ? 'success' : 'default'}
                    size="small"
                  />
                  {tenant.subscription_tier === 'trial' && (
                    <Chip
                      label={tenant.trial_ends_at && new Date(tenant.trial_ends_at) < new Date() ? 'Trial Expired' : 'Trial'}
                      color={tenant.trial_ends_at && new Date(tenant.trial_ends_at) < new Date() ? 'error' : 'warning'}
                      size="small" variant="outlined"
                    />
                  )}
                  {tenant.subscription_tier && tenant.subscription_tier !== 'trial' && (
                    <Chip
                      label={tenant.subscription_tier.charAt(0).toUpperCase() + tenant.subscription_tier.slice(1)}
                      color="primary" size="small" variant="outlined"
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>{tenant.user_count || 0}</TableCell>
              <TableCell>{tenant.employee_count || 0}</TableCell>
              <TableCell>
                {new Date(tenant.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell align="right">
                {tenant.subscription_tier === 'trial' && (
                  <Tooltip title="Grant Full Access — removes trial restrictions">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={async () => {
                        try {
                          await updateTenant(tenant.id, { grant_full_access: true });
                          setSuccess(`Full access granted to ${tenant.display_name}`);
                          loadTenants();
                        } catch (err) {
                          setError(err.response?.data?.error || 'Failed to grant access');
                        }
                      }}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="View Users">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenUsersDialog(tenant)}
                  >
                    <PeopleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Email Settings">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEmailDialog(tenant)}
                  >
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Auth Settings">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenAuthDialog(tenant)}
                  >
                    <SecurityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEditDialog(tenant)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Deactivate">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setSelectedTenant(tenant);
                      setDeleteDialogOpen(true);
                    }}
                    disabled={!tenant.is_active}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {tenants.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  No tenants found. Create your first tenant to get started.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const selectedProfile = profiles.find((p) => p.key === formData.profile);

  return (
    <Box sx={{ p: isMobile ? 2 : 3, pb: isMobile ? 10 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
          {isMobile ? 'Tenants' : 'Tenant Management'}
        </Typography>
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadTenants}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEditDialog()}
            >
              Add Tenant
            </Button>
          </Box>
        )}
        {isMobile && (
          <IconButton onClick={loadTenants} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        isMobile ? renderMobileView() : renderDesktopView()
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 72, right: 16 }}
          onClick={() => handleOpenEditDialog()}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Edit/Create Tenant Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {selectedTenant ? 'Edit Tenant' : 'Create New Tenant'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name/Slug"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase() })}
                error={!!formErrors.name}
                helperText={formErrors.name || 'Used in URLs (lowercase, no spaces)'}
                disabled={!!selectedTenant} // Can't change slug after creation
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                error={!!formErrors.display_name}
                helperText={formErrors.display_name || 'Shown in the UI'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            {!selectedTenant && profiles.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    select
                    label="Starting profile"
                    value={formData.profile}
                    onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText={selectedProfile?.description || ''}
                  >
                    {profiles.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                  </TextField>
                  <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={<ProfileSummary profile={selectedProfile} />}>
                    <IconButton size="small" sx={{ ml: 0.5, mt: 1 }} aria-label="What this profile sets">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            )}
            {!selectedTenant && profilesFailed && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Starting profiles couldn't be loaded — the tenant will be created with the Standard profile.
                </Typography>
              </Grid>
            )}

            {!selectedTenant && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Optional: Create Admin User
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Admin Username"
                    value={formData.admin_username}
                    onChange={(e) => setFormData({ ...formData, admin_username: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Admin Password"
                    type="password"
                    value={formData.admin_password}
                    onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                    error={!!formErrors.admin_password}
                    helperText={formErrors.admin_password}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Admin Email"
                    type="email"
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveTenant} variant="contained">
            {selectedTenant ? 'Save Changes' : 'Create Tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users Dialog */}
      <Dialog open={usersDialogOpen} onClose={() => setUsersDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Users in {selectedTenant?.display_name}
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenantUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>
                      <Chip label={u.role} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {u.first_name && u.last_name
                        ? `${u.first_name} ${u.last_name}`
                        : u.first_name || u.last_name || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.is_active ? 'Active' : 'Inactive'}
                        color={u.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {u.last_login
                        ? new Date(u.last_login).toLocaleString()
                        : 'Never'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          onClick={() => handleResetUserPassword(selectedTenant.id, u.id)}
                          disabled={resettingUserId === u.id}
                        >
                          {resettingUserId === u.id ? <CircularProgress size={18} /> : <LockResetIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {tenantUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Email Settings Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Email Settings for {selectedTenant?.display_name}
        </DialogTitle>
        <DialogContent>
          {emailSettings && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Provider Selector */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Email Provider"
                  value={emailSettings.email_provider}
                  onChange={(e) => setEmailSettings({ ...emailSettings, email_provider: e.target.value })}>
                  <MenuItem value="smtp">SMTP</MenuItem>
                  <MenuItem value="resend">Resend</MenuItem>
                  <MenuItem value="graph">Microsoft 365 (Graph API)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailSettings.enabled || false}
                      onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                    />
                  }
                  label="Email Enabled"
                />
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* SMTP Settings */}
              {emailSettings.email_provider === 'smtp' && (
                <>
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth label="SMTP Host"
                      value={emailSettings.smtp_host || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField fullWidth label="Port" type="number"
                      value={emailSettings.smtp_port || 587}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })} />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={<Switch checked={emailSettings.smtp_secure || false}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_secure: e.target.checked })} />}
                      label="SSL/TLS"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="SMTP Username"
                      value={emailSettings.smtp_user || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="SMTP Password" type="password"
                      value={emailSettings.smtp_pass || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_pass: e.target.value })}
                      helperText="Leave blank to keep existing" />
                  </Grid>
                </>
              )}

              {/* Resend Settings */}
              {emailSettings.email_provider === 'resend' && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Resend API Key" type="password"
                      value={emailSettings.resend_api_key || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, resend_api_key: e.target.value })}
                      helperText="Leave blank to keep existing" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="From Domain"
                      value={emailSettings.resend_from_domain || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, resend_from_domain: e.target.value })}
                      helperText="e.g. company.com — must be verified in Resend" />
                  </Grid>
                </>
              )}

              {/* Microsoft 365 Graph Settings */}
              {emailSettings.email_provider === 'graph' && (
                <>
                  <Grid item xs={12}>
                    <Tooltip title="The Azure AD / Entra tenant ID for the M365 organization">
                      <TextField fullWidth label="Azure Tenant ID"
                        value={emailSettings.graph_tenant_id || ''}
                        onChange={(e) => setEmailSettings({ ...emailSettings, graph_tenant_id: e.target.value })} />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <Tooltip title="App Registration Client ID with Mail.Send permission">
                      <TextField fullWidth label="Client ID"
                        value={emailSettings.graph_client_id || ''}
                        onChange={(e) => setEmailSettings({ ...emailSettings, graph_client_id: e.target.value })} />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Client Secret" type="password"
                      value={emailSettings.graph_client_secret || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, graph_client_secret: e.target.value })}
                      helperText="Leave blank to keep existing" />
                  </Grid>
                  <Grid item xs={12}>
                    <Tooltip title="The M365 mailbox that sends emails (must be a licensed user or shared mailbox)">
                      <TextField fullWidth label="Sender Email"
                        value={emailSettings.graph_sender || ''}
                        onChange={(e) => setEmailSettings({ ...emailSettings, graph_sender: e.target.value })}
                        placeholder="hourtrax@company.com" />
                    </Tooltip>
                  </Grid>
                </>
              )}

              <Grid item xs={12}><Divider /></Grid>

              {/* Common fields */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="From Email"
                  value={emailSettings.from_email || ''}
                  onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                  helperText="Display address on outgoing emails" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="From Name"
                  value={emailSettings.from_name || ''}
                  onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEmailSettings} variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Deactivate Tenant</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate <strong>{selectedTenant?.display_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Users will no longer be able to log in to this tenant. This action can be reversed by editing the tenant.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTenant} color="error" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Result Dialog */}
      <Dialog open={!!resetResult} onClose={() => setResetResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Password Reset Successful</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Password has been reset for <strong>{resetResult?.username}</strong>.
            {resetResult?.emailSent
              ? ' A reset email has been sent.'
              : ' No email was sent (user has no email on file or email is not configured).'}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Temporary password:
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'grey.50',
              fontFamily: 'monospace',
              fontSize: '1.1rem',
            }}
          >
            <Typography fontFamily="monospace" fontSize="1.1rem">
              {resetResult?.tempPassword}
            </Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(resetResult?.tempPassword || '');
                  setSuccess('Password copied to clipboard');
                }}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The user will be required to change their password on next login.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetResult(null)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auth Settings Dialog */}
      <Dialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="primary" />
          Authentication Settings — {selectedTenant?.display_name}
          <Tooltip title="Configure how users authenticate with this tenant. You can use local username/password with optional MFA, or federated login via Microsoft Entra ID (Azure AD).">
            <IconButton size="small"><HelpIcon fontSize="small" /></IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          {authSettings && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* Auth Method Selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Authentication Method
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant={authSettings.auth_method !== 'oidc' ? 'contained' : 'outlined'}
                    onClick={() => setAuthSettings({ ...authSettings, auth_method: 'local' })}
                    sx={{ flex: 1 }}
                  >
                    Local (Password)
                  </Button>
                  <Button
                    variant={authSettings.auth_method === 'oidc' ? 'contained' : 'outlined'}
                    onClick={() => setAuthSettings({ ...authSettings, auth_method: 'oidc' })}
                    sx={{ flex: 1 }}
                  >
                    Entra ID (OIDC)
                  </Button>
                </Box>
              </Grid>

              {/* Local Auth + MFA Options */}
              {authSettings.auth_method !== 'oidc' && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <Typography variant="subtitle2">MFA Policy</Typography>
                      <Tooltip title="Multi-Factor Authentication (MFA) adds a second layer of security. Users will need an authenticator app (Google Authenticator, Microsoft Authenticator, or Authy) to generate a 6-digit code each time they sign in.">
                        <IconButton size="small"><HelpIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={authSettings.mfa_admin_required || false}
                          onChange={(e) => setAuthSettings({ ...authSettings, mfa_admin_required: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Require MFA for Admins</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Admin and Program Admin users must set up MFA before accessing the system
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={authSettings.mfa_required || false}
                          onChange={(e) => setAuthSettings({ ...authSettings, mfa_required: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Require MFA for All Users</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Every user in this tenant must set up MFA. Overrides the admin-only setting
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        How MFA works for users:
                      </Typography>
                      <Typography variant="body2" component="ol" sx={{ pl: 2, mb: 0 }}>
                        <li>Users log in with their password as usual</li>
                        <li>If MFA is required but not yet set up, they are prompted to configure it</li>
                        <li>They scan a QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, Authy)</li>
                        <li>On each future login, they enter the 6-digit code from their app</li>
                        <li>Backup codes are provided in case the user loses access to their authenticator app</li>
                      </Typography>
                    </Alert>
                  </Grid>
                </>
              )}

              {/* OIDC / Entra ID Configuration */}
              {authSettings.auth_method === 'oidc' && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <Typography variant="subtitle2">Microsoft Entra ID Configuration</Typography>
                      <Tooltip title="Entra ID (formerly Azure AD) allows users to sign in with their Microsoft work account. MFA is managed by your Entra ID tenant policies, not by HourTrax.">
                        <IconButton size="small"><HelpIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        How to set up Entra ID:
                      </Typography>
                      <Typography variant="body2" component="ol" sx={{ pl: 2, mb: 0 }}>
                        <li>Go to <strong>Azure Portal</strong> &gt; Microsoft Entra ID &gt; App registrations &gt; New registration</li>
                        <li>Name: "HourTrax" (or any name you prefer)</li>
                        <li>Supported account types: "Accounts in this organizational directory only"</li>
                        <li>Redirect URI: Web — <code>{window.location.origin}/api/auth/oidc/callback</code></li>
                        <li>After creation, copy the <strong>Application (client) ID</strong> and <strong>Directory (tenant) ID</strong></li>
                        <li>Go to Certificates &amp; secrets &gt; New client secret — copy the secret <strong>Value</strong> (not the ID)</li>
                        <li>Go to API permissions &gt; Add permission &gt; Microsoft Graph &gt; Delegated &gt; <strong>openid</strong>, <strong>profile</strong>, <strong>email</strong></li>
                        <li>Click "Grant admin consent" for the permissions</li>
                      </Typography>
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Entra ID Tenant ID"
                      value={authSettings.oidc_tenant_id || ''}
                      onChange={(e) => setAuthSettings({ ...authSettings, oidc_tenant_id: e.target.value.trim() })}
                      helperText="Directory (tenant) ID from Azure Portal — found on the app registration Overview page"
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Client ID"
                      value={authSettings.oidc_client_id || ''}
                      onChange={(e) => setAuthSettings({ ...authSettings, oidc_client_id: e.target.value.trim() })}
                      helperText="Application (client) ID from the app registration Overview page"
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Client Secret"
                      type="password"
                      value={authSettings.oidc_client_secret || ''}
                      onChange={(e) => setAuthSettings({ ...authSettings, oidc_client_secret: e.target.value })}
                      helperText={
                        authSettings.oidc_client_secret_set
                          ? 'A secret is already configured. Leave blank to keep the existing value, or enter a new one to replace it'
                          : 'Client secret Value from Certificates & secrets (not the secret ID)'
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={authSettings.oidc_auto_provision !== false}
                          onChange={(e) => setAuthSettings({ ...authSettings, oidc_auto_provision: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Auto-provision users</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Automatically create HourTrax accounts for new Entra ID users on first sign-in.
                            Users are created with the "user" role and can be promoted by an admin
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      <Typography variant="body2">
                        <strong>Note:</strong> When switching to Entra ID, existing local users can still sign in
                        with their password. Users who sign in via Entra ID will have their accounts linked
                        automatically if their email matches, or a new account will be created if auto-provision is enabled.
                        MFA is managed by your Entra ID policies when using this method.
                      </Typography>
                    </Alert>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveAuthSettings}
            variant="contained"
            disabled={authSaving}
            startIcon={authSaving ? <CircularProgress size={16} /> : null}
          >
            {authSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TenantManagement;
